<template>
  <div class="gl-search-wrap" ref="wrap">
    <form class="gl-search-form" :class="{ 'gl-search-focused': isOpen }" action="#" method="get" @submit.prevent="goSelected">
      <svg class="gl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        ref="input"
        class="gl-search-input"
        type="text"
        v-model="terms"
        @input="onInput"
        @keydown.up.prevent="moveUp"
        @keydown.down.prevent="moveDown"
        @keydown.enter.prevent="goSelected"
        @keydown.esc="close"
        @focus="onFocus"
        placeholder="Search pages..."
        autocomplete="off"
      />
      <button v-if="terms" class="gl-search-clear" @click="close" type="button">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <kbd v-else class="gl-search-kbd">⌘K</kbd>
    </form>

    <!-- Dropdown -->
    <transition name="gl-dropdown">
      <div v-if="isOpen" class="gl-search-dropdown" ref="dropdown">

        <!-- Results -->
        <template v-if="menuItems.length">
          <div class="gl-dropdown-label">Pages</div>
          <router-link
            v-for="(item, i) in menuItems.slice(0, 8)"
            :key="i"
            :to="{ path: item.path }"
            class="gl-result-item"
            :class="{ 'gl-result-active': activeIndex === i }"
            @click="close"
            @mouseenter="activeIndex = i"
          >
            <div class="gl-result-icon-wrap">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <div class="gl-result-body">
              <span class="gl-result-title" v-html="highlight(item.title)"></span>
              <span class="gl-result-path">{{ item.path }}</span>
            </div>
            <div class="gl-result-enter" :class="{ 'gl-enter-visible': activeIndex === i }">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>
            </div>
          </router-link>
        </template>

        <!-- Empty -->
        <div v-else class="gl-search-empty">
          <div class="gl-empty-icon-wrap">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <p class="gl-empty-msg">No results for <strong>"{{ terms }}"</strong></p>
          <p class="gl-empty-hint">Try searching for a page name or feature</p>
        </div>

        <!-- Footer -->
        <div class="gl-dropdown-footer">
          <span class="gl-foot-hint"><kbd>↑↓</kbd> Navigate</span>
          <span class="gl-foot-hint"><kbd>↵</kbd> Open</span>
          <span class="gl-foot-hint"><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'SearchBar',
  data() {
    return {
      terms: '',
      isOpen: false,
      activeIndex: 0,
    };
  },
  computed: {
    ...mapState({ _allResults: (state) => state.menu.searchData }),
    menuItems() {
      const auth = useAuthStore();
      if (auth.isSuperAdmin) return this._allResults;
      return this._allResults.filter(item => !item.path?.startsWith('/super-admin'));
    },
  },
  watch: {
    menuItems() { this.activeIndex = 0; },
  },
  mounted() {
    this._cmdK = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        this.$refs.input.focus();
        this.isOpen = true;
      }
    };
    this._outside = (e) => {
      if (this.$refs.wrap && !this.$refs.wrap.contains(e.target)) this.close();
    };
    window.addEventListener('keydown', this._cmdK);
    document.addEventListener('mousedown', this._outside);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this._cmdK);
    document.removeEventListener('mousedown', this._outside);
  },
  methods: {
    onInput() {
      this.$store.dispatch('menu/searchTerm', this.terms);
      this.isOpen = !!this.terms;
    },
    onFocus() {
      if (this.terms) this.isOpen = true;
    },
    close() {
      this.isOpen = false;
      this.terms = '';
      this.$store.dispatch('menu/searchTerm', '');
      this.activeIndex = 0;
      this.$refs.input?.blur();
    },
    moveUp() {
      if (this.activeIndex > 0) this.activeIndex--;
    },
    moveDown() {
      const max = Math.min(this.menuItems.length, 8) - 1;
      if (this.activeIndex < max) this.activeIndex++;
    },
    goSelected() {
      const item = this.menuItems[this.activeIndex];
      if (item) {
        this.$router.push({ path: item.path });
        this.close();
      }
    },
    highlight(text) {
      if (!this.terms) return text;
      const escaped = this.terms.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark class="gl-match">$1</mark>');
    },
  },
};
</script>

<style scoped>
.gl-search-wrap {
  position: relative;
  width: 100%;
  max-width: 320px;
}

/* Search bar */
.gl-search-form {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f7;
  border: 1.5px solid transparent;
  border-radius: 10px;
  padding: 0 12px;
  height: 38px;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.gl-search-form:focus-within,
.gl-search-focused {
  background: #fff;
  border-color: #900C27;
  box-shadow: 0 0 0 3px rgba(144,12,39,.1);
}
.gl-search-icon { color: #9ca3af; flex-shrink: 0; transition: color .15s; }
.gl-search-form:focus-within .gl-search-icon { color: #900C27; }
.gl-search-input {
  flex: 1; border: none; background: transparent;
  font-size: 13.5px; color: #111318; font-weight: 500;
  outline: none; min-width: 0; padding: 0;
}
.gl-search-input::placeholder { color: #9ca3af; font-weight: 400; }
.gl-search-kbd {
  font-size: 10.5px; font-weight: 600; color: #9ca3af;
  background: #e5e7eb; border: 1px solid #d1d5db;
  border-radius: 5px; padding: 2px 5px; line-height: 1.4;
  flex-shrink: 0; font-family: inherit; white-space: nowrap;
}
.gl-search-clear {
  display: flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #d1d5db; border: none; color: #6b7280;
  cursor: pointer; flex-shrink: 0; padding: 0;
  transition: background .12s;
}
.gl-search-clear:hover { background: #9ca3af; color: #fff; }

/* Dropdown */
.gl-search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 380px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06);
  overflow: hidden;
  z-index: 9999;
}

.gl-dropdown-label {
  font-size: 10.5px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: .6px;
  padding: 12px 14px 6px;
}

/* Result items */
.gl-result-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 14px;
  text-decoration: none;
  border-radius: 0;
  transition: background .1s;
  cursor: pointer;
}
.gl-result-item:hover,
.gl-result-active { background: #f0f8f5; }

.gl-result-icon-wrap {
  width: 30px; height: 30px; border-radius: 8px;
  background: #f1f5f9; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; transition: background .1s, color .1s;
}
.gl-result-active .gl-result-icon-wrap,
.gl-result-item:hover .gl-result-icon-wrap {
  background: rgba(144,12,39,.12); color: #900C27;
}

.gl-result-body { flex: 1; min-width: 0; }
.gl-result-title {
  display: block; font-size: 13.5px; font-weight: 600;
  color: #1e293b; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis;
}
.gl-result-path {
  display: block; font-size: 11px; color: #94a3b8;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}

.gl-result-enter {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px;
  background: rgba(144,12,39,.1); color: #900C27;
  flex-shrink: 0; opacity: 0; transition: opacity .1s;
}
.gl-enter-visible { opacity: 1; }

/* Empty state */
.gl-search-empty {
  padding: 28px 16px; text-align: center;
}
.gl-empty-icon-wrap {
  width: 44px; height: 44px; border-radius: 12px;
  background: #f1f5f9; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 12px;
}
.gl-empty-msg { font-size: 13.5px; color: #334155; margin: 0 0 4px; }
.gl-empty-hint { font-size: 12px; color: #94a3b8; margin: 0; }

/* Footer */
.gl-dropdown-footer {
  display: flex; align-items: center; gap: 14px;
  padding: 9px 14px;
  border-top: 1px solid #f0f2f5;
  background: #fafafa;
}
.gl-foot-hint {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; color: #94a3b8;
}
.gl-foot-hint kbd {
  font-size: 10px; font-weight: 600; color: #64748b;
  background: #f1f5f9; border: 1px solid #e2e8f0;
  border-radius: 4px; padding: 1px 5px; font-family: inherit;
}

/* Transition */
.gl-dropdown-enter-active { transition: opacity .15s, transform .15s; }
.gl-dropdown-leave-active { transition: opacity .1s, transform .1s; }
.gl-dropdown-enter-from { opacity: 0; transform: translateY(-6px); }
.gl-dropdown-leave-to   { opacity: 0; transform: translateY(-4px); }
</style>

<style>
/* Highlight match */
.gl-match {
  background: rgba(144,12,39,.15);
  color: #900C27;
  border-radius: 3px;
  padding: 0 1px;
  font-style: normal;
}

/* Dark mode */
body.dark-only .gl-search-form { background: rgba(255,255,255,.06); border-color: transparent; }
body.dark-only .gl-search-form:focus-within { background: rgba(255,255,255,.1); border-color: #900C27; box-shadow: 0 0 0 3px rgba(144,12,39,.15); }
body.dark-only .gl-search-input { color: rgba(255,255,255,.87); }
body.dark-only .gl-search-input::placeholder { color: rgba(255,255,255,.3); }
body.dark-only .gl-search-kbd { background: rgba(255,255,255,.08); border-color: #3d4255; color: rgba(255,255,255,.3); }
body.dark-only .gl-search-dropdown { background: #1a2535; border-color: #243040; box-shadow: 0 8px 32px rgba(0,0,0,.4); }
body.dark-only .gl-result-item:hover,
body.dark-only .gl-result-active { background: rgba(144,12,39,.12); }
body.dark-only .gl-result-icon-wrap { background: #243040; color: #94a3b8; }
body.dark-only .gl-result-title { color: #f1f5f9; }
body.dark-only .gl-result-path { color: #64748b; }
body.dark-only .gl-dropdown-footer { background: #0f1923; border-color: #243040; }
body.dark-only .gl-foot-hint kbd { background: #1a2535; border-color: #243040; color: #94a3b8; }
body.dark-only .gl-empty-icon-wrap { background: #1e2d3d; }
body.dark-only .gl-empty-msg { color: #cbd5e1; }
body.dark-only .gl-dropdown-label { color: #4a6080; }
</style>
